import {
  DEFAULT_THEME_NEUTRAL,
  DEFAULT_THEME_PRIMARY,
  type NeutralThemeColor,
  type PrimaryThemeColor,
} from '~/composables/useBuilderMeta'

const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const

/** CSS var token for a Tailwind palette name (neutral → old-neutral, matching Nuxt UI). */
function paletteToken(name: string): string {
  return name === 'neutral' ? 'old-neutral' : name
}

export type ThemeSurfaceMode = 'light' | 'dark'

/**
 * Scoped Nuxt UI color vars for a preview / site root.
 * Mirrors `app.config.ts` → `ui.colors` and wires surface tokens so Neutral
 * changes the main background (Nuxt UI light mode otherwise keeps `--ui-bg: #fff`).
 */
export function themeCssVarsFromPalette(
  primary: string = DEFAULT_THEME_PRIMARY,
  neutral: string = DEFAULT_THEME_NEUTRAL,
  mode: ThemeSurfaceMode = 'light',
): Record<string, string> {
  const p = paletteToken(primary)
  const n = paletteToken(neutral)
  const vars: Record<string, string> = {
    '--ui-primary': 'var(--ui-color-primary-500)',
    '--ui-secondary': 'var(--ui-color-secondary-500)',
  }

  for (const shade of SHADES) {
    vars[`--ui-color-primary-${shade}`] = `var(--color-${p}-${shade})`
    vars[`--ui-color-neutral-${shade}`] = `var(--color-${n}-${shade})`
    vars[`--color-primary-${shade}`] = `var(--color-${p}-${shade})`
  }

  if (mode === 'dark') {
    vars['--ui-bg'] = `var(--color-${n}-900)`
    vars['--ui-bg-muted'] = `var(--color-${n}-800)`
    vars['--ui-bg-elevated'] = `var(--color-${n}-800)`
    vars['--ui-bg-accented'] = `var(--color-${n}-700)`
    vars['--ui-bg-inverted'] = `var(--color-${n}-50)`
    vars['--ui-text-dimmed'] = `var(--color-${n}-500)`
    vars['--ui-text-muted'] = `var(--color-${n}-400)`
    vars['--ui-text-toned'] = `var(--color-${n}-300)`
    vars['--ui-text'] = `var(--color-${n}-200)`
    vars['--ui-text-highlighted'] = `var(--color-${n}-50)`
    vars['--ui-text-inverted'] = `var(--color-${n}-900)`
    vars['--ui-border'] = `var(--color-${n}-800)`
    vars['--ui-border-muted'] = `var(--color-${n}-700)`
    vars['--ui-border-accented'] = `var(--color-${n}-700)`
    vars['--ui-border-inverted'] = `var(--color-${n}-50)`
  }
  else {
    // Tint the canvas with neutral-50 so slate / stone / olive / … are visible.
    vars['--ui-bg'] = `var(--color-${n}-50)`
    vars['--ui-bg-muted'] = `var(--color-${n}-100)`
    vars['--ui-bg-elevated'] = `#fff`
    vars['--ui-bg-accented'] = `var(--color-${n}-200)`
    vars['--ui-bg-inverted'] = `var(--color-${n}-900)`
    vars['--ui-text-dimmed'] = `var(--color-${n}-400)`
    vars['--ui-text-muted'] = `var(--color-${n}-500)`
    vars['--ui-text-toned'] = `var(--color-${n}-600)`
    vars['--ui-text'] = `var(--color-${n}-700)`
    vars['--ui-text-highlighted'] = `var(--color-${n}-900)`
    vars['--ui-text-inverted'] = `#fff`
    vars['--ui-border'] = `var(--color-${n}-200)`
    vars['--ui-border-muted'] = `var(--color-${n}-200)`
    vars['--ui-border-accented'] = `var(--color-${n}-300)`
    vars['--ui-border-inverted'] = `var(--color-${n}-900)`
  }

  return vars
}

/** Apply site brand colors to runtime `app.config` (same keys as `app.config.ts`). */
export function applyAppConfigTheme(
  primary: PrimaryThemeColor | string = DEFAULT_THEME_PRIMARY,
  neutral: NeutralThemeColor | string = DEFAULT_THEME_NEUTRAL,
): void {
  updateAppConfig({
    ui: {
      colors: {
        primary,
        neutral,
      },
    },
  })
}

/** Restore platform defaults from `app.config.ts`. */
export function resetAppConfigTheme(): void {
  applyAppConfigTheme(DEFAULT_THEME_PRIMARY, DEFAULT_THEME_NEUTRAL)
}

/**
 * On published tenant pages / builder, sync Nuxt UI semantic colors to the site palette.
 * Restores platform defaults when the watcher stops (route leave / unmount).
 */
export function useSiteUiTheme(
  primary: MaybeRefOrGetter<string | undefined>,
  neutral: MaybeRefOrGetter<string | undefined>,
) {
  if (import.meta.server) {
    applyAppConfigTheme(
      toValue(primary) || DEFAULT_THEME_PRIMARY,
      toValue(neutral) || DEFAULT_THEME_NEUTRAL,
    )
  }

  watch(
    () => [toValue(primary), toValue(neutral)] as const,
    ([p, n]) => {
      applyAppConfigTheme(p || DEFAULT_THEME_PRIMARY, n || DEFAULT_THEME_NEUTRAL)
    },
    { immediate: true },
  )

  onBeforeUnmount(() => {
    resetAppConfigTheme()
  })
}
